import {
  gridPaginationModelSelector,
  gridRowCountSelector,
  gridRowsLoadingSelector,
  useGridApiContext,
} from "@mui/x-data-grid";
import { useGridSelector } from "@mui/x-data-grid/internals";
import PaginationInfo from "./PaginationInfo";
import PaginationActions, {
  GridPaginationChangePageAction,
} from "./PaginationActions";
import { useEffect, useState } from "react";
import { Select, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

export interface GridPaginationProps {
  className?: string;
  approximate?: boolean;
  startApproximatingAt?: number;
  estimatedRowCount?: number;
  rowCount?: number;
  onNextPage?: GridPaginationChangePageAction;
  onPreviousPage?: GridPaginationChangePageAction;
  pageIndex?: number;
  pageSizeOptions?: readonly (number | { value: number; label: string })[];
  pageSize: number;
  onPageSizeChange?: (pageSize: number) => void;
}

const GridPagination = ({
  className = "",
  approximate = false,
  startApproximatingAt = 0,
  estimatedRowCount,
  rowCount,
  onNextPage,
  onPreviousPage,
  pageIndex,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100], //default fallback
}: GridPaginationProps) => {
  const { t } = useTranslation("common");
  const apiRef = useGridApiContext();
  const [page, setPage] = useState<number>(0);
  const muiRowCount = useGridSelector(apiRef, gridRowCountSelector);
  const gridPaginationModel = useGridSelector(
    apiRef,
    gridPaginationModelSelector
  );
  pageSize = pageSize ?? gridPaginationModel.pageSize;
  const isLoading = useGridSelector(apiRef, gridRowsLoadingSelector);

  useEffect(() => {
    setPage(pageIndex ?? 0);
  }, [pageIndex]);

  const handleNextPage = () => {
    setPage((prev) => prev + 1);
    onNextPage?.();
  };

  const handlePreviousPage = () => {
    setPage((prev) => prev - 1);
    onPreviousPage?.();
  };

  const actualRowCount = estimatedRowCount
    ? undefined
    : rowCount ?? muiRowCount;

  return (
    <div className={`${className} w-min text-nowrap flex-nowrap h-full flex`}>
      <div className="flex items-center mx-8">
        <Typography marginX="10px" variant="body2">
          {t("common:rowsPerPage")}
        </Typography>
        <Select
          disabled={isLoading}
          value={pageSize}
          onChange={(e) => {
            const newSize = Number(e.target.value);
            apiRef.current.setPageSize(newSize);
            onPageSizeChange?.(newSize);
          }}
          size="small"
          style={{ marginRight: 8 }}
        >
          {pageSizeOptions.map((size) => {
            const value = typeof size === "number" ? size : size.value;
            const label = typeof size === "number" ? `${size}` : size.label;
            return (
              <MenuItem key={value} value={value}>
                <Typography variant="body2">{label}</Typography>
              </MenuItem>
            );
          })}
        </Select>
      </div>
      <PaginationInfo
        className="pr-2"
        page={page}
        rowCount={actualRowCount}
        estimatedRowCount={estimatedRowCount}
        rowsPerPage={pageSize}
        approximate={approximate}
        startApproximatingAt={startApproximatingAt}
        isLoading={isLoading}
      />
      <PaginationActions
        page={page}
        rowCount={actualRowCount}
        estimatedRowCount={estimatedRowCount}
        rowsPerPage={pageSize}
        isLoading={isLoading}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
      />
    </div>
  );
};

export default GridPagination;
